import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { SessionService } from 'src/app/auth/session/session.service';
import { Cohort, CohortService } from 'src/app/cohorts/services/cohort.service';
import { ModuleRef } from 'src/app/shared/module-search/module-search.component';
import { Module } from '../../modules/services/firmware/firmware.service';
import {
  Phase,
  Release,
  ReleaseFirmwareVersion,
  ReleaseRequest,
  ReleaseService,
  SimulateResponse,
} from '../services/release.service';

@Component({
  selector: 'dcc-create-release',
  templateUrl: './create-release.component.html',
  styleUrls: ['./create-release.component.scss'],
})
export class CreateReleaseComponent implements OnInit {
  @Output() refreshPage: EventEmitter<any> = new EventEmitter();
  @Output() closePage: EventEmitter<any> = new EventEmitter();

  releaseId: string;
  releaseName: string;
  releaseDescription: string;
  releaseCreator: string;
  isRollingOut = '';
  createdDate: string;
  deletedDate: string;
  status: string;
  releaseToUpdate: Release;
  clickSuccess = false;
  isUpdate = false;
  pageTitle = 'Create Release';
  statusCss: string;
  selectedPhases: Phase[] = [];
  estimatedTotals: SimulateResponse = {} as SimulateResponse;
  allCohorts: Cohort[] = [];
  lastEstimatedCohorts: string[] = [];
  lastEstimatedModules: ModuleRef[] = [];

  selectedModules: ModuleRef[] = [];
  lcFlex: string[] = ['SF300', 'SF300W', 'SF200', 'SF64', 'SF1'];
  otto: string[] = ['SF500', 'SF400', 'SF400V2', 'SF400V2W', 'SF300V2'];
  selectedFirmwareVersions: ReleaseFirmwareVersion[] = [];
  constructor(
    private activeModal: NgbActiveModal,
    private releaseService: ReleaseService,
    private sessionService: SessionService,
    private cohortService: CohortService,
    private toastr: ToastrService,
  ) {}

  async ngOnInit() {
    this.allCohorts = await this.cohortService.getActiveCohorts();

    if (this.releaseToUpdate !== undefined) {
      this.isUpdate = true;
      this.pageTitle = 'Update Release';
      this.loadData();
      this.tryEstimateReleaseTotal();
    } else {
      this.selectedPhases.push({
        name: 'Phase ' + (this.selectedPhases.length + 1),
        cohorts: [],
        status: 'SCHEDULED',
        expectedReleaseDate: null,
        description: '',
      });
    }
  }

  updateModules(modules: ModuleRef[]) {
    this.selectedModules = modules;
    this.tryEstimateReleaseTotal();
  }

  updateFirmware(firmwareVersions: ReleaseFirmwareVersion[]) {
    this.selectedFirmwareVersions = firmwareVersions;
    this.tryEstimateReleaseTotal();
  }

  tryEstimateReleaseTotal(index?: number) {
    const filledOutPhases = [
      ...this.selectedPhases.filter(
        (phase) =>
          phase.cohorts.length > 0 && phase.expectedReleaseDate !== null,
      ),
    ];

    let filledOutApps = [
      ...this.selectedModules.filter(
        (module) =>
          module.name !== null &&
          module.version !== null &&
          module.platform !== null,
      ),
    ];

    filledOutApps = filledOutApps.concat(
      this.convertFirmwareToApps(this.selectedFirmwareVersions),
    );

    const hasPhases = filledOutPhases.length > 0;

    const hasApps =
      filledOutApps.length > 0 || this.selectedFirmwareVersions.length > 0;

    if (
      hasPhases &&
      hasApps &&
      (index === undefined ? true : this.selectedModules[index].version)
    ) {
      this.calculateEstimatedTotals(filledOutPhases, filledOutApps);
    } else {
      this.estimatedTotals = {} as SimulateResponse;
      this.lastEstimatedCohorts = [];
      this.lastEstimatedModules = [];
    }
  }

  close() {
    if (this.isUpdate) {
      this.closePage.emit();
    }
    this.activeModal.close(false);
  }

  async create() {
    const release: ReleaseRequest = {
      name: this.releaseName,
      description: this.releaseDescription,
      releaseCreator: this.getUserName(),
      phases: this.selectedPhases,
      modules: this.formatModules(this.selectedModules),
      firmware: this.formatFirmware(this.selectedFirmwareVersions),
    };

    try {
      await this.releaseService.createRelease(release);
      this.toastr.success('Created Release: ' + this.releaseName);
      this.refreshPage.emit();
      this.close();
    } catch (e) {
      this.clickSuccess = false;
      this.toastr.error('Failed to Create the Release :' + e.error.message);
    }
  }

  isValidRelease() {
    const operationType = this.isUpdate ? 'update' : 'create';
    if (this.releaseName === undefined || this.releaseName === '') {
      this.toastr.error(
        `Cannot ${operationType === 'create' ? 'Create' : 'Update'} Release Without Release Name`,
      );
      return false;
    }
    if (
      this.selectedPhases.some(
        ({ cohorts, expectedReleaseDate }) => !cohorts || !expectedReleaseDate,
      )
    ) {
      this.toastr.error(
        `Cannot ${operationType === 'create' ? 'Create' : 'Update'} Release Without valid Phases: Cohorts and Expected Release Date are required`,
      );
      return false;
    }
    if (
      this.selectedPhases[0].cohorts === undefined ||
      this.selectedPhases[0].cohorts.length === 0
    ) {
      this.toastr.error(
        `Cannot ${operationType === 'create' ? 'Create' : 'Update'} Release Without any Phases selected`,
      );
      return false;
    }
    if (
      this.selectedModules.length === 0 &&
      this.selectedFirmwareVersions.length === 0
    ) {
      this.toastr.error(
        `Cannot ${operationType === 'create' ? 'Create' : 'Update'} Release Without any Modules or Firmwares selected`,
      );
      return false;
    }
    const isInvalidModule = this.selectedModules.some(
      ({ name, version, platform }) => !name || !version || !platform,
    );
    if (isInvalidModule) {
      this.toastr.error(
        `Cannot ${operationType === 'create' ? 'Create' : 'Update'} Release Without valid Modules selected: Module Name, Version, and Platform are required`,
      );
      return false;
    }
    if (
      this.releaseDescription === undefined ||
      this.releaseDescription === ''
    ) {
      this.toastr.error(
        `Cannot ${operationType === 'create' ? 'Create' : 'Update'} Release Without Release Description`,
      );
      return false;
    }
    if (this.selectedModules.some((module) => !this.isJsonValid(module))) {
      this.toastr.error(
        `Cannot ${operationType === 'create' ? 'Create' : 'Update'} Release Without valid JSON Configuration`,
      );
      return false;
    }
    return true;
  }

  addPhase() {
    this.selectedPhases.push({
      name: 'Phase ' + (this.selectedPhases.length + 1),
      cohorts: [],
      status: 'Scheduled',
      description: '',
      expectedReleaseDate: null,
    });
  }

  removePhase(index?: number) {
    this.selectedPhases.pop();
    this.tryEstimateReleaseTotal(index);
  }

  blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, _) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.readAsText(blob);
    });
  }

  onExpectedReleaseDateChange(expectedReleaseDate, index: number) {
    this.selectedPhases[index].expectedReleaseDate = expectedReleaseDate;
    this.tryEstimateReleaseTotal();
  }

  getCurrentDate() {
    const today = new Date();
    const year = today.getFullYear();
    let month = (today.getMonth() + 1).toString();
    let day = today.getDate().toString();

    if (month.length < 2) {
      month = '0' + month;
    }
    if (day.length < 2) {
      day = '0' + day;
    }
    return `${year}-${month}-${day}`;
  }

  async handleClick() {
    if (!this.isValidRelease()) {
      return;
    }
    this.clickSuccess = true;
    if (this.isUpdate) {
      await this.update();
    } else {
      await this.create();
    }
  }

  isJsonValid(module: ModuleRef): boolean {
    try {
      if (module.configuration && module.configurationSchema) {
        JSON.parse(module.configuration);
      }
    } catch (e) {
      return false;
    }
    return true;
  }

  async update() {
    const release: Release = {
      id: this.releaseId,
      name: this.releaseName,
      description: this.releaseDescription,
      releaseCreator: this.releaseCreator,
      requestCreator: this.getUserName(),
      phases: this.selectedPhases,
      modules: this.formatModules(this.selectedModules),
      firmware: this.formatFirmware(this.selectedFirmwareVersions),
      isRollingOut: this.isRollingOut ? this.isRollingOut : '',
      createdDate: this.createdDate,
      deletedDate: this.deletedDate,
      status: this.status,
    };
    try {
      await this.releaseService.updateRelease(release);
      this.toastr.success('Updated Release: ' + this.releaseName);
      this.refreshPage.emit();
      this.closePage.emit();
      this.close();
    } catch (e) {
      this.clickSuccess = false;
      this.toastr.error('Failed to Update the Release : ' + e.error.message);
    }
  }

  cohortExistsInOtherPhases(cohortName: string, currentIndex: number): boolean {
    return this.selectedPhases.some(
      (phase, index) =>
        index !== currentIndex && phase.cohorts.includes(cohortName),
    );
  }

  private formatModules(moduleRef: ModuleRef[]): Module[] {
    return moduleRef.map((m) => {
      const module: Module = {
        platform: this.determinePlatform(m.hardwarePlatform ?? m.platform),
        hardwarePlatform: this.determineHardwardPlatform(
          m.hardwarePlatform ?? m.platform,
        ),
        name: m.name,
        version: m.version,
        configuration: m.configuration,
        configurationSchema: m.configurationSchema,
      };
      return module;
    });
  }

  private formatFirmware(
    firmwareList: ReleaseFirmwareVersion[],
  ): ReleaseFirmwareVersion[] {
    return firmwareList.map((f) => {
      const firmware: ReleaseFirmwareVersion = {
        platform: this.determinePlatform(f.hardwarePlatform ?? f.platform),
        hardwarePlatform: this.determineHardwardPlatform(
          f.hardwarePlatform ?? f.platform,
        ),
        name: f.name,
        version: f.version,
        build: f.build,
        firmwareId: f.firmwareId,
      };
      return firmware;
    });
  }

  private determinePlatform(platform: string): string {
    return platform === 'LcFlex' || platform === 'Otto'
      ? platform
      : this.determinePlatformFromDeviceType(platform);
  }

  private determinePlatformFromDeviceType(platform: string): string {
    return this.lcFlex.includes(platform) ? 'LcFlex' : 'Otto';
  }

  private determineHardwardPlatform(platform: string): string {
    return platform === 'LcFlex' || platform === 'Otto' ? undefined : platform;
  }

  private getUserName(): string {
    return this.sessionService.getCurrentSession().name;
  }

  private async calculateEstimatedTotals(
    phases: Phase[],
    modulesRef: ModuleRef[],
  ) {
    const cohorts = [...phases.map((phase) => phase.cohorts).flat()];
    const modules = this.formatModules(modulesRef);

    this.estimatedTotals = await this.releaseService.simulateRelease({
      phases,
      modules,
    });
    this.estimatedTotals.invalidReleaseEntities.forEach((entity) => {
      this.toastr.warning(
        `Invalid ${entity.type} in cohort ${entity.cohortName}: ${entity.id}`,
        'Invalid Release Entity',
      );
    });

    this.lastEstimatedCohorts = [...cohorts];
    this.lastEstimatedModules = [...modulesRef];
  }

  private loadData(): void {
    this.releaseId = this.releaseToUpdate.id;
    this.releaseName = this.releaseToUpdate.name;
    this.releaseDescription = this.releaseToUpdate.description;
    this.releaseCreator = this.releaseToUpdate.releaseCreator;
    this.selectedPhases = this.releaseToUpdate.phases;
    this.selectedModules = (this.releaseToUpdate.modules as ModuleRef[]) ?? [];
    this.selectedFirmwareVersions = this.releaseToUpdate.firmware ?? [];
    this.createdDate = this.releaseToUpdate.createdDate;
    this.isRollingOut = this.releaseToUpdate.isRollingOut;
    this.deletedDate = this.releaseToUpdate.deletedDate;
    this.status = this.releaseToUpdate.status;
  }

  private convertFirmwareToApps(
    firmware: ReleaseFirmwareVersion[],
  ): ModuleRef[] {
    return firmware.map((f) => {
      const module: ModuleRef = {
        name: f.name,
        version: f.version,
        platform: f.platform,
        configuration: null,
        configurationSchema: null,
      };
      return module;
    });
  }
}
